import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Drawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import MuiLink from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Receipt from '@mui/icons-material/esm/Receipt';
import HourglassEmpty from '@mui/icons-material/esm/HourglassEmpty';
import CheckCircle from '@mui/icons-material/esm/CheckCircle';
import AttachMoney from '@mui/icons-material/esm/AttachMoney';
import SendIcon from '@mui/icons-material/esm/Send';
import CosignIcon from '@mui/icons-material/esm/HowToReg';
import PaymentsIcon from '@mui/icons-material/esm/Payments';
import { useSelector } from 'react-redux';

import { KPICard, DataTable, Column } from '../../components/common';
import { PageHeader } from '../../components/layout';
import { RootState } from '../../store';
import drawdownService, {
  DrawdownPacketRow,
  DrawdownPacketWithItems,
  DrawdownPacketStatus,
} from '../../services/api/drawdownService';

const STATUS_LABEL: Record<DrawdownPacketStatus, string> = {
  snapshot_created: 'Staged',
  submitted: 'Submitted',
  approved: 'Approved',
  rejected: 'Rejected',
  paid: 'Paid',
  voided: 'Voided',
};

const STATUS_COLOR: Record<DrawdownPacketStatus, 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'> = {
  snapshot_created: 'info',
  submitted: 'warning',
  approved: 'primary',
  rejected: 'error',
  paid: 'success',
  voided: 'default',
};

const formatMoney = (value: number): string => `$${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const formatDate = (value: string | null): string => (value ? new Date(value).toLocaleDateString() : '—');

interface DrawdownsPageProps {
  embedded?: boolean;
}

export default function DrawdownsPage({ embedded = false }: DrawdownsPageProps = {}) {
  const activeProjectId = useSelector((state: RootState) => state.projects.activeProjectId);
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const role = currentUser?.role ?? '';
  const canSubmit = role === 'admin' || role === 'accounts';
  const canCosign = role === 'admin' || role === 'pm';
  const canMarkPaid = role === 'admin' || role === 'accounts';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [packets, setPackets] = useState<DrawdownPacketRow[]>([]);
  const [statusFilter, setStatusFilter] = useState<DrawdownPacketStatus | 'all'>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedPacket, setSelectedPacket] = useState<DrawdownPacketWithItems | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await drawdownService.listPackets({
          projectId: activeProjectId ?? undefined,
          status: statusFilter === 'all' ? undefined : statusFilter,
          limit: 100,
        });
        if (!cancelled) setPackets(result.packets);
      } catch (err: any) {
        if (!cancelled) setError(err.response?.data?.message || 'Failed to load drawdown packets');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeProjectId, statusFilter]);

  useEffect(() => {
    // Reset transient action feedback when switching packets so a stale
    // success/error toast from the last packet doesn't carry over.
    setActionError(null);
    setActionSuccess(null);
    if (!selectedId) {
      setSelectedPacket(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setDetailLoading(true);
        setDetailError(null);
        const result = await drawdownService.getPacket(selectedId);
        if (!cancelled) setSelectedPacket(result);
      } catch (err: any) {
        if (!cancelled) setDetailError(err.response?.data?.message || 'Failed to load packet detail');
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedId]);

  const refreshAfterAction = async () => {
    // Re-pull both the list (so status chip updates) and the detail (so
    // timeline/cosign state updates) — fastest way to reflect the
    // backend's state-machine transitions without manual local mutation.
    try {
      const [list, detail] = await Promise.all([
        drawdownService.listPackets({
          projectId: activeProjectId ?? undefined,
          status: statusFilter === 'all' ? undefined : statusFilter,
          limit: 100,
        }),
        selectedId ? drawdownService.getPacket(selectedId) : Promise.resolve(null),
      ]);
      setPackets(list.packets);
      if (detail) setSelectedPacket(detail);
    } catch {
      // Soft fail — toast already shown by action handler.
    }
  };

  const handleAction = async (
    action: 'submitInitiate' | 'submitCosign' | 'markPaid',
    successMessage: string
  ) => {
    if (!selectedId) return;
    try {
      setActionLoading(action);
      setActionError(null);
      setActionSuccess(null);
      if (action === 'submitInitiate') await drawdownService.submitInitiate(selectedId);
      else if (action === 'submitCosign') await drawdownService.submitCosign(selectedId);
      else if (action === 'markPaid') await drawdownService.markPaid(selectedId);
      setActionSuccess(successMessage);
      await refreshAfterAction();
    } catch (err: any) {
      setActionError(err.response?.data?.message || `Failed to ${action}`);
    } finally {
      setActionLoading(null);
    }
  };

  const summary = useMemo(() => {
    const totals = {
      count: packets.length,
      staged: 0,
      submitted: 0,
      approved: 0,
      paid: 0,
      stagedValue: 0,
      awaitingValue: 0,
      paidValue: 0,
    };
    for (const p of packets) {
      if (p.status === 'snapshot_created') { totals.staged += 1; totals.stagedValue += p.netAmount; }
      else if (p.status === 'submitted') { totals.submitted += 1; totals.awaitingValue += p.netAmount; }
      else if (p.status === 'approved') { totals.approved += 1; totals.awaitingValue += p.netAmount; }
      else if (p.status === 'paid') { totals.paid += 1; totals.paidValue += p.netAmount; }
    }
    return totals;
  }, [packets]);

  const columns: Column<DrawdownPacketRow>[] = [
    { id: 'drawdownReference', label: 'Reference', minWidth: 140 },
    {
      id: 'status',
      label: 'Status',
      minWidth: 130,
      format: (value: DrawdownPacketStatus) => (
        <Chip size="small" label={STATUS_LABEL[value] ?? value} color={STATUS_COLOR[value] ?? 'default'} />
      ),
    },
    {
      id: 'project',
      label: 'Project',
      minWidth: 180,
      format: (_value: any, row: DrawdownPacketRow) => row.project?.name ?? row.projectId ?? '—',
    },
    {
      id: 'netAmount',
      label: 'Net Amount',
      minWidth: 130,
      align: 'right',
      format: (value: number) => formatMoney(value),
    },
    {
      id: 'itemCount',
      label: 'Items',
      minWidth: 70,
      align: 'right',
    },
    {
      id: 'cosignRequired',
      label: 'Cosign',
      minWidth: 110,
      format: (_value: boolean, row: DrawdownPacketRow) => {
        if (!row.cosignRequired) return '—';
        if (row.cosignCompletedAt) return <Chip size="small" label="Completed" color="success" />;
        if (row.cosignInitiatedAt) return <Chip size="small" label="Initiated" color="warning" />;
        return <Chip size="small" label="Required" color="default" variant="outlined" />;
      },
    },
    {
      id: 'submittedAt',
      label: 'Submitted',
      minWidth: 110,
      format: (value: string | null) => formatDate(value),
    },
    {
      id: 'paidAt',
      label: 'Paid',
      minWidth: 110,
      format: (value: string | null) => formatDate(value),
    },
  ];

  return (
    <Box>
      {!embedded && (
        <PageHeader
          title="Drawdowns"
          subtitle={packets.length > 0 ? `${packets.length} packet${packets.length === 1 ? '' : 's'} loaded` : undefined}
        />
      )}

      {/* Summary KPIs */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Staged"
            value={`${summary.staged}`}
            changeLabel={summary.staged > 0 ? formatMoney(summary.stagedValue) + ' net' : 'No drawdowns queued'}
            icon={<Receipt />}
            color={summary.staged > 0 ? 'info' : 'success'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Awaiting Payment"
            value={`${summary.submitted + summary.approved}`}
            changeLabel={summary.submitted + summary.approved > 0
              ? `${formatMoney(summary.awaitingValue)} (${summary.submitted} submitted / ${summary.approved} approved)`
              : 'All caught up'}
            icon={<HourglassEmpty />}
            color={summary.submitted + summary.approved > 0 ? 'warning' : 'success'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Paid"
            value={`${summary.paid}`}
            changeLabel={summary.paid > 0 ? formatMoney(summary.paidValue) + ' received' : 'None paid yet'}
            icon={<CheckCircle />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Total Net"
            value={formatMoney(packets.reduce((sum, p) => sum + p.netAmount, 0))}
            changeLabel={`${packets.length} packet${packets.length === 1 ? '' : 's'} in view`}
            icon={<AttachMoney />}
            color="primary"
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value as any)}>
            <MenuItem value="all">All statuses</MenuItem>
            {(Object.keys(STATUS_LABEL) as DrawdownPacketStatus[]).map((s) => (
              <MenuItem key={s} value={s}>{STATUS_LABEL[s]}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {!activeProjectId && (
          <Typography variant="caption" color="text.secondary">
            No active project selected — showing all visible projects.
          </Typography>
        )}
      </Box>

      {/* Body */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : packets.length === 0 ? (
        <Alert severity="info">No drawdown packets match the current filters.</Alert>
      ) : (
        <Box sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
          <DataTable
            columns={columns}
            data={packets}
            pagination
            searchable
            onRowClick={(row) => setSelectedId(row.id)}
          />
        </Box>
      )}

      {/* Detail drawer */}
      <Drawer anchor="right" open={!!selectedId} onClose={() => setSelectedId(null)}>
        {selectedId && (
          <Box sx={{ width: 480, p: 3 }}>
            {detailLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress />
              </Box>
            ) : detailError ? (
              <Alert severity="error">{detailError}</Alert>
            ) : selectedPacket ? (
              <>
                <Typography variant="h6" fontWeight="bold">{selectedPacket.drawdownReference}</Typography>
                <Box sx={{ mt: 1, mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip size="small" label={STATUS_LABEL[selectedPacket.status] ?? selectedPacket.status} color={STATUS_COLOR[selectedPacket.status] ?? 'default'} />
                  <Chip size="small" label={`v${selectedPacket.snapshotVersion}`} variant="outlined" />
                  {selectedPacket.cosignRequired && (
                    <Chip
                      size="small"
                      label={selectedPacket.cosignCompletedAt ? 'Cosign done' : selectedPacket.cosignInitiatedAt ? 'Cosign initiated' : 'Cosign required'}
                      color={selectedPacket.cosignCompletedAt ? 'success' : selectedPacket.cosignInitiatedAt ? 'warning' : 'default'}
                    />
                  )}
                </Box>

                {(canSubmit || canCosign || canMarkPaid) && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom>Actions</Typography>
                    {actionError && (
                      <Alert severity="error" sx={{ mb: 1 }}>{actionError}</Alert>
                    )}
                    {actionSuccess && (
                      <Alert severity="success" sx={{ mb: 1 }}>{actionSuccess}</Alert>
                    )}
                    <Stack spacing={1}>
                      {canSubmit && selectedPacket.status === 'snapshot_created' && (
                        <Button
                          variant="contained"
                          startIcon={<SendIcon />}
                          disabled={actionLoading !== null}
                          onClick={() => handleAction('submitInitiate', selectedPacket.cosignRequired ? 'Submission initiated — awaiting cosign.' : 'Packet submitted.')}
                        >
                          {actionLoading === 'submitInitiate' ? 'Submitting…' : selectedPacket.cosignRequired ? 'Initiate submission' : 'Submit'}
                        </Button>
                      )}
                      {canCosign && selectedPacket.status === 'snapshot_created' && selectedPacket.cosignRequired && selectedPacket.cosignInitiatedAt && !selectedPacket.cosignCompletedAt && (
                        <Button
                          variant="contained"
                          color="warning"
                          startIcon={<CosignIcon />}
                          disabled={actionLoading !== null}
                          onClick={() => handleAction('submitCosign', 'Cosign completed — packet submitted.')}
                        >
                          {actionLoading === 'submitCosign' ? 'Cosigning…' : 'Cosign + submit'}
                        </Button>
                      )}
                      {canMarkPaid && selectedPacket.status === 'approved' && (
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<PaymentsIcon />}
                          disabled={actionLoading !== null}
                          onClick={() => handleAction('markPaid', 'Packet marked as paid.')}
                        >
                          {actionLoading === 'markPaid' ? 'Marking…' : 'Mark paid'}
                        </Button>
                      )}
                      {selectedPacket.status === 'snapshot_created' && selectedPacket.cosignRequired && !selectedPacket.cosignInitiatedAt && !canSubmit && (
                        <Alert severity="info">Awaiting Accounts to initiate submission.</Alert>
                      )}
                      {selectedPacket.status === 'snapshot_created' && selectedPacket.cosignRequired && selectedPacket.cosignInitiatedAt && !selectedPacket.cosignCompletedAt && !canCosign && (
                        <Alert severity="info">Awaiting PM cosign before the packet submits.</Alert>
                      )}
                      {selectedPacket.status === 'submitted' && (
                        <Alert severity="info">Submitted to JMB — awaiting approval. No actions available until they approve.</Alert>
                      )}
                      {selectedPacket.status === 'approved' && !canMarkPaid && (
                        <Alert severity="info">Awaiting Accounts to record payment receipt.</Alert>
                      )}
                      {selectedPacket.status === 'paid' && (
                        <Alert severity="success">Paid {formatDate(selectedPacket.paidAt)} — no further actions.</Alert>
                      )}
                    </Stack>
                  </>
                )}

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2">Amounts</Typography>
                <Typography variant="body2">Gross: {formatMoney(selectedPacket.grossAmount)}</Typography>
                <Typography variant="body2">Retention: {formatMoney(selectedPacket.retentionAmount)}</Typography>
                <Typography variant="body2"><strong>Net: {formatMoney(selectedPacket.netAmount)}</strong></Typography>
                <Typography variant="body2">{selectedPacket.itemCount} item{selectedPacket.itemCount === 1 ? '' : 's'} in {selectedPacket.groupCount} group{selectedPacket.groupCount === 1 ? '' : 's'}</Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2">Timeline</Typography>
                <Typography variant="body2">Snapshot generated: {new Date(selectedPacket.snapshotGeneratedAt).toLocaleString()}</Typography>
                <Typography variant="body2">Submitted: {formatDate(selectedPacket.submittedAt)}</Typography>
                <Typography variant="body2">Approved: {formatDate(selectedPacket.approvedAt)}</Typography>
                <Typography variant="body2">Paid: {formatDate(selectedPacket.paidAt)}</Typography>
                {selectedPacket.voidedAt && (
                  <Typography variant="body2" color="error.main">Voided: {formatDate(selectedPacket.voidedAt)}{selectedPacket.voidReason ? ` — ${selectedPacket.voidReason}` : ''}</Typography>
                )}
                {selectedPacket.pdfDocument && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2">Packet PDF</Typography>
                    <MuiLink href={selectedPacket.pdfDocument.fileUrl} target="_blank" rel="noreferrer">
                      {selectedPacket.pdfDocument.title}
                    </MuiLink>
                  </>
                )}
                {selectedPacket.items && selectedPacket.items.length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom>Items</Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Description</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Net</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedPacket.items.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell>
                              <Typography variant="body2">{item.description}</Typography>
                              {item.task && (
                                <Typography variant="caption" color="text.secondary">{item.task.taskCode}</Typography>
                              )}
                            </TableCell>
                            <TableCell>{item.status}</TableCell>
                            <TableCell align="right">{formatMoney(item.netAmount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                )}
              </>
            ) : (
              <Alert severity="info">Packet not found.</Alert>
            )}
          </Box>
        )}
      </Drawer>
    </Box>
  );
}
